import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  useTheme,
  Alert,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Email,
  ArrowBack,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

import { authService } from '@/services/auth/authService';

export const VerifyEmailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Verification token is missing');
      setLoading(false);
      return;
    }

    const verifyEmail = async () => {
      try {
        setLoading(true);
        const response = await authService.verifyEmail(token);
        
        if (response.success) {
          setSuccess(true);
          toast.success('Email verified successfully!');
          
          // Start countdown
          const timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate('/login');
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          return () => clearInterval(timer);
        } else {
          throw new Error(response.message || 'Email verification failed');
        }
      } catch (err: any) {
        setError(err.message || 'Email verification failed');
        toast.error(err.message || 'Email verification failed');
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'light'
          ? `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`
          : `linear-gradient(135deg, ${theme.palette.primary.dark}25, ${theme.palette.secondary.dark}25)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 480,
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.mode === 'light' 
            ? 'rgba(255, 255, 255, 0.95)' 
            : 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                bgcolor: success 
                  ? 'success.main' 
                  : error 
                    ? 'error.main' 
                    : 'primary.main',
                color: 'white',
              }}
            >
              {loading ? (
                <CircularProgress size={40} color="inherit" />
              ) : success ? (
                <CheckCircle sx={{ fontSize: 40 }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 40 }} />
              )}
            </Box>
            
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              {loading ? 'Verifying Email...' : success ? 'Email Verified!' : 'Verification Failed'}
            </Typography>
            
            <Typography variant="body1" color="text.secondary">
              {loading 
                ? 'Please wait while we verify your email address' 
                : success 
                  ? 'Your email has been successfully verified'
                  : 'There was an issue verifying your email'
              }
            </Typography>
          </Box>

          {/* Content */}
          {loading && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                This may take a few moments...
              </Typography>
            </Box>
          )}

          {success && (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Your email has been verified successfully! You can now log in to your account.
                </Typography>
              </Alert>
              
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Redirecting to login page in {countdown} second{countdown !== 1 ? 's' : ''}...
                </Typography>
              </Box>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {error}
              </Typography>
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ textAlign: 'center' }}>
            {success ? (
              <Button
                component={Link}
                to="/login"
                variant="contained"
                size="large"
                startIcon={<Email />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                }}
              >
                Go to Login
              </Button>
            ) : !loading && (
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                size="large"
                startIcon={<ArrowBack />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Back to Login
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};